import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { LegendComponent, TitleComponent } from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { GridComponent } from 'echarts/components';
echarts.use([
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  PieChart,
  GridComponent,
  CanvasRenderer
]);

interface NutritionFactInterFace {
  value: any;
  nutrientName: string;
  unitName: string;
}
interface NutrientInterFace {
  nutritionFact: Array<NutritionFactInterFace>;
  quantity: string;
  name: string;
  servingSize: number;
  servingSizeUnit: string;
}

interface TotalNutrients {
  [key: string]: {
    value: number;
    unitName: string;
  };
}

interface TotalEnergy {
  value: number;
  name: string;
}

const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  chartData: any,
  totalEnergy: TotalEnergy
) => {
  const isMobile = window.innerWidth < 768;

  return {
    color: ['#ED4D18', '#1ACEDA', '#FEB91A', '#4E7CB6', '#A1A1A1'],
    tooltip: {
      trigger: 'item',
      formatter: function (params: any) {
        return `${params.data.name}: ${params.data.value.toFixed(2)}${
          params.data.unitName
        }`;
      }
    },
    legend: {
      orient: isMobile ? 'horizontal' : 'vertical',
      right: isMobile ? 'center' : '15%', // Center legend on mobile
      top: isMobile ? 'auto' : 'center',
      bottom: isMobile ? 5 : 'auto',
      textStyle: {
        fontSize: isMobile ? 12 : 14,
        color: '#333'
      },
      formatter: function (name: string) {
        const item = chartData.find((i: any) => i.name === name);
        return `${item.name}: ${item.value.toFixed(2)} ${item.unitName}`;
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    series: [
      {
        name: 'Total Calories',
        type: 'pie',
        radius: isMobile ? ['40%', '60%'] : ['50%', '70%'],
        center: isMobile ? ['50%', '35%'] : ['25%', '50%'],
        startAngle: 90, // Adjust angle if needed
        avoidLabelOverlap: true,
        label: {
          show: true,
          position: 'center',
          formatter: `{b|${totalEnergy.value.toFixed(2)}}\n{a|Total Kcal}`, // Show total in center
          rich: {
            b: {
              fontSize: isMobile ? 20 : 28,
              fontWeight: 800,
              color: '#333'
            },
            a: {
              fontSize: isMobile ? 12 : 14,
              fontWeight: 600,
              color: '#666'
            }
          }
        },
        emphasis: {
          label: {
            show: true
          }
        },
        labelLine: {
          show: false
        },
        data: chartData // Dynamic data
      }
    ]
  };
};

const getFormatedNutrient = (nutrient: Array<NutrientInterFace>) => {
  const totalNutrients: TotalNutrients = {};
  // Loop through each food item
  nutrient.forEach((item: any) => {
    item.nutritionFact.forEach((nutrient: NutritionFactInterFace) => {
      const { nutrientName, value, unitName } = nutrient;
      if (nutrientName !== 'Energy') {
        if (!totalNutrients[nutrientName]) {
          totalNutrients[nutrientName] = { value: 0, unitName };
        }
        totalNutrients[nutrientName].value += (value * item.quantity) / 100;
      }
    });
  });

  // Convert the result to an array of objects
  const totalNutritionArray = Object.keys(totalNutrients).map(nutrientName => ({
    value: totalNutrients[nutrientName].value,
    name: nutrientName,
    unitName: totalNutrients[nutrientName].unitName
  }));
  return totalNutritionArray;
};

const getTotalEnergy = (nutrient: Array<NutrientInterFace>): TotalEnergy => {
  const totalNutrients: TotalNutrients = {};
  // Loop through each food item
  nutrient.forEach((item: any) => {
    item.nutritionFact.forEach((nutrient: NutritionFactInterFace) => {
      const { nutrientName, value, unitName } = nutrient;
      if (nutrientName === 'Energy' && unitName === 'kcal') {
        if (!totalNutrients[nutrientName]) {
          totalNutrients[nutrientName] = { value: 0, unitName };
        }
        totalNutrients[nutrientName].value += (value * item.quantity) / 100;
      }
    });
  });

  // Convert the result to an array of objects
  const totalNutritionArray = Object.keys(totalNutrients).map(nutrientName => ({
    value: totalNutrients[nutrientName].value,
    name: nutrientName
  }));
  return totalNutritionArray[0];
};
const FoodPaichart = (props: any) => {
  const { getThemeColor } = useAppContext();
  const chartData = getFormatedNutrient(props.nutrient);
  const totalEnergy = getTotalEnergy(props.nutrient);

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getDefaultOptions(getThemeColor, chartData, totalEnergy)}
      style={{ minHeight: '280px' }}
    />
  );
};

export default FoodPaichart;
